/**
 * Enterprise-grade Request Manager
 *
 * Features for billion-scale:
 * - Request deduplication (prevents duplicate in-flight requests)
 * - LRU cache with TTL
 * - Retry with exponential backoff
 * - Request cancellation via AbortController
 * - Rate limiting awareness
 * - Request batching for list operations
 */

class LRUCache {
  constructor(maxSize = 100, defaultTTL = 60000) {
    this.maxSize = maxSize;
    this.defaultTTL = defaultTTL;
    this.cache = new Map();
  }

  get(key) {
    const entry = this.cache.get(key);
    if (!entry) return undefined;

    if (Date.now() > entry.expiresAt) {
      this.cache.delete(key);
      return undefined;
    }

    // Move to end (most recently used)
    this.cache.delete(key);
    this.cache.set(key, entry);
    return entry.value;
  }

  set(key, value, ttl = this.defaultTTL) {
    // Evict oldest if at capacity
    if (this.cache.size >= this.maxSize) {
      const oldestKey = this.cache.keys().next().value;
      this.cache.delete(oldestKey);
    }

    this.cache.set(key, {
      value,
      expiresAt: Date.now() + ttl,
    });
  }

  invalidate(key) {
    this.cache.delete(key);
  }

  invalidatePattern(pattern) {
    const regex = new RegExp(pattern);
    for (const key of this.cache.keys()) {
      if (regex.test(key)) {
        this.cache.delete(key);
      }
    }
  }

  clear() {
    this.cache.clear();
  }
}

class RequestManager {
  constructor() {
    this.cache = new LRUCache(200, 30000); // 200 items, 30s default TTL
    this.inFlight = new Map(); // Deduplication map
    this.abortControllers = new Map();
    this.retryConfig = {
      maxRetries: 3,
      baseDelay: 1000,
      maxDelay: 10000,
    };
  }

  /**
   * Generate cache key from endpoint and params
   */
  getCacheKey(endpoint, params = {}) {
    const sortedParams = Object.keys(params)
      .sort()
      .map(k => `${k}=${JSON.stringify(params[k])}`)
      .join('&');
    return `${endpoint}?${sortedParams}`;
  }

  /**
   * Execute request with deduplication and caching
   */
  async execute(requestFn, options = {}) {
    const {
      cacheKey,
      cacheTTL = 30000,
      skipCache = false,
      deduplicate = true,
      retryable = true,
      abortKey,
    } = options;

    // Check cache first
    if (cacheKey && !skipCache) {
      const cached = this.cache.get(cacheKey);
      if (cached !== undefined) {
        return cached;
      }
    }

    // Check for in-flight duplicate
    if (deduplicate && cacheKey && this.inFlight.has(cacheKey)) {
      return this.inFlight.get(cacheKey);
    }

    // Setup abort controller
    let abortController;
    if (abortKey) {
      // Cancel previous request with same key
      this.abort(abortKey);
      abortController = new AbortController();
      this.abortControllers.set(abortKey, abortController);
    }

    // Create the request promise
    const requestPromise = this.executeWithRetry(
      requestFn,
      retryable ? this.retryConfig.maxRetries : 0,
      abortController?.signal
    );

    // Store in deduplication map
    if (deduplicate && cacheKey) {
      this.inFlight.set(cacheKey, requestPromise);
    }

    try {
      const result = await requestPromise;

      // Cache the result
      if (cacheKey) {
        this.cache.set(cacheKey, result, cacheTTL);
      }

      return result;
    } finally {
      // Clean up
      if (cacheKey) {
        this.inFlight.delete(cacheKey);
      }
      if (abortKey) {
        this.abortControllers.delete(abortKey);
      }
    }
  }

  /**
   * Execute with exponential backoff retry
   */
  async executeWithRetry(requestFn, retriesLeft, signal) {
    try {
      return await requestFn(signal);
    } catch (error) {
      // Don't retry aborted requests
      if (error.name === 'AbortError') {
        throw error;
      }

      // Don't retry client errors (4xx except 429)
      if (error.status >= 400 && error.status < 500 && error.status !== 429) {
        throw error;
      }

      if (retriesLeft <= 0) {
        throw error;
      }

      // Calculate delay with exponential backoff + jitter
      const attempt = this.retryConfig.maxRetries - retriesLeft;
      const delay = Math.min(
        this.retryConfig.baseDelay * Math.pow(2, attempt) + Math.random() * 1000,
        this.retryConfig.maxDelay
      );

      // Handle rate limiting
      if (error.status === 429 && error.retryAfter) {
        await this.sleep(error.retryAfter * 1000);
      } else {
        await this.sleep(delay);
      }

      return this.executeWithRetry(requestFn, retriesLeft - 1, signal);
    }
  }

  /**
   * Abort a request by key
   */
  abort(key) {
    const controller = this.abortControllers.get(key);
    if (controller) {
      controller.abort();
      this.abortControllers.delete(key);
    }
  }

  /**
   * Abort all requests
   */
  abortAll() {
    for (const controller of this.abortControllers.values()) {
      controller.abort();
    }
    this.abortControllers.clear();
  }

  /**
   * Invalidate cache entries
   */
  invalidateCache(pattern) {
    if (pattern) {
      this.cache.invalidatePattern(pattern);
    } else {
      this.cache.clear();
    }
  }

  sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

// Singleton instance
export const requestManager = new RequestManager();

export default RequestManager;
